import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    getWorkingUnitClassifications,
    updateWorkingUnitClassification,
    WorkingUnitClassification,
} from '../../services/budgetWorkingUnitClassification';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';

export function useWorkingUnitClassifications(): [
    WorkingUnitClassification[],
    boolean,
    { isError: boolean; error: Error | null; refetch: () => void },
] {
    const {
        data: classifications,
        isLoading,
        isFetching,
        isFetchedAfterMount,
        isError,
        error,
        refetch,
    } = useQuery<WorkingUnitClassification[], Error>({
        queryKey: [QueryKey.BudgetWorkingUnitClassifications],
        queryFn: getWorkingUnitClassifications,
        initialData: [],
    });

    return [
        classifications,
        isFetchedAfterMount ? isLoading : isFetching,
        { isError, error: error ?? null, refetch: () => refetch() },
    ];
}

export function useUpdateWorkingUnitClassification() {
    const { t } = useTranslation('budget');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation({
        mutationFn: updateWorkingUnitClassification,
        onSuccess: () => {
            dispatchSuccessfullySavedRecord(t('workingUnits.title'));
        },
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message });
        },
        // CALL-780: on settle, not on success. The Billing Config grid now edits this inline, and ag-grid
        // has already written the new value into its row by the time the request answers — so a FAILED
        // PATCH left the unsaved value on screen while the server still held null, and the grid's own
        // "editable only while empty" rule then made the cell read-only, with no way back until some
        // unrelated refetch. Refetching either way puts server truth back in front of the user.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetWorkingUnitClassifications] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.WorkingUnitLocations] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs] });
        },
    });
}
